import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitSignupRequest } from '../services/signupRequestService';
import { assetUrl } from '../utils/assets';
import SelectField from './SelectField';
import Modal from './Modal';
import './Login.css';

const ROLE_OPTIONS = [
  'Government Officer',
  'Executor',
  'Founder',
  'Auditor',
  'Compliance Officer',
  'Regulator',
  'Other',
];

const ROLE_SELECT_OPTIONS = [
  { value: '', label: 'Select your role' },
  ...ROLE_OPTIONS.map((r) => ({ value: r, label: r })),
];

const normalizeWebsite = (value) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const emptySignupForm = () => ({
  name: '',
  email: '',
  company: '',
  role: '',
  website: '',
  extraDetails: '',
  companyUrl: '', // honeypot — leave empty
});

const Login = ({ onLogin, error: externalError, inviteSuccess }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupSuccessMessage, setSignupSuccessMessage] = useState(null);
  const [signupForm, setSignupForm] = useState(emptySignupForm);
  const [signupSubmitting, setSignupSubmitting] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [roleInvalid, setRoleInvalid] = useState(false);
  const [preferReducedMotion, setPreferReducedMotion] = useState(false);
  const dismissTimerRef = useRef(null);
  const signupOpenedAtRef = useRef(null);

  useEffect(() => {
    document.title = 'Log in · Compliance Dashboard';
  }, []);

  useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPreferReducedMotion(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  useEffect(() => () => {
    if (dismissTimerRef.current) {
      window.clearTimeout(dismissTimerRef.current);
    }
  }, []);

  const openSignupModal = () => {
    setSignupForm(emptySignupForm());
    setSignupError('');
    setSignupSuccessMessage(null);
    setRoleInvalid(false);
    signupOpenedAtRef.current = Date.now();
    setSignupOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onLogin(email, password);
    } catch (err) {
      setError(err.message || 'Failed to log in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupFieldChange = (field, value) => {
    setSignupForm((prev) => ({ ...prev, [field]: value }));
    setSignupError('');
    if (field === 'role') setRoleInvalid(false);
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setSignupError('');
    if (!signupForm.role || !ROLE_OPTIONS.includes(signupForm.role)) {
      setRoleInvalid(true);
      setSignupError('Please select your role.');
      return;
    }
    setSignupSubmitting(true);
    try {
      const openedAt = signupOpenedAtRef.current;
      if (!openedAt || Date.now() - openedAt < 3000) {
        setSignupError('Please wait a moment and submit again.');
        setSignupSubmitting(false);
        return;
      }
      const result = await submitSignupRequest({
        name: signupForm.name.trim(),
        email: signupForm.email.trim().toLowerCase(),
        company: signupForm.company.trim(),
        role: signupForm.role,
        website: normalizeWebsite(signupForm.website) || null,
        extraDetails: signupForm.extraDetails.trim() || null,
        companyUrl: signupForm.companyUrl,
        formOpenedAt: openedAt,
      });
      const message = result?.deduped
        ? 'A request for this email is already pending review. We will email you when it is processed.'
        : 'Thank you. Your request has been submitted. We will review it and send you an invite link.';
      setSignupSuccessMessage(message);
      setSignupForm(emptySignupForm());
      if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = window.setTimeout(() => {
        setSignupOpen(false);
        setSignupSuccessMessage(null);
      }, result?.deduped ? 8000 : 6000);
    } catch (err) {
      setSignupError(err.message || 'Failed to submit signup request. Please try again.');
    } finally {
      setSignupSubmitting(false);
    }
  };

  const closeSignupModal = () => {
    if (!signupSubmitting) {
      if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
      setSignupOpen(false);
      setSignupError('');
      setSignupSuccessMessage(null);
      setRoleInvalid(false);
      signupOpenedAtRef.current = null;
    }
  };

  const logoSrc = assetUrl('logo.png');
  const videoSrc = assetUrl('grey.mp4');

  return (
    <div className="login-page">
      <div className="login-page-logo" aria-hidden="true">
        <img src={logoSrc} alt="" width={96} height={96} />
      </div>
      <div className="login-visual">
        {preferReducedMotion ? (
          <div className="login-video login-video--static" aria-hidden="true" />
        ) : (
          <video
            className="login-video"
            src={videoSrc}
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
          />
        )}
      </div>

      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>Compliance Dashboard</h1>
            <p>Confidential Token Transfer Auditing</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form" aria-busy={loading}>
            {inviteSuccess && (
              <div className="login-success-message ui-alert ui-alert--success" role="status" aria-live="polite">
                <span>Password set. You can now log in with your new password.</span>
                <button
                  type="button"
                  className="ui-btn ui-btn--ghost"
                  onClick={() => navigate('/', { replace: true })}
                >
                  Dismiss
                </button>
              </div>
            )}
            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                placeholder="auditor@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="Enter your password"
              />
              <p className="form-hint">Use your invite link if you still need to set a password.</p>
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Logging in…' : 'Log In'}
            </button>

            <div className="login-buttons-divider" role="separator" aria-hidden="true">
              <span>or</span>
            </div>

            <button type="button" className="signup-link-button" onClick={openSignupModal}>
              Sign up
            </button>
          </form>
        </div>
      </div>

      {signupOpen && (
        <Modal
          title="Sign up"
          onClose={closeSignupModal}
          dismissible={!signupSubmitting}
        >
          {signupSuccessMessage ? (
            <div className="signup-success-message ui-alert ui-alert--success" role="status" aria-live="polite">
              {signupSuccessMessage}
              <div className="modal-actions">
                <button type="button" className="button-primary" onClick={closeSignupModal}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSignupSubmit} className="signup-form" aria-busy={signupSubmitting}>
              {signupError && (
                <div className="error-message" role="alert">
                  {signupError}
                </div>
              )}
              {/* Honeypot: hidden from users; bots that autofill it are discarded server-side. */}
              <div className="signup-honeypot" aria-hidden="true">
                <label htmlFor="signup-company-url">Company URL</label>
                <input
                  type="text"
                  id="signup-company-url"
                  name="companyUrl"
                  value={signupForm.companyUrl}
                  onChange={(e) => handleSignupFieldChange('companyUrl', e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="signup-name">Name</label>
                <input
                  type="text"
                  id="signup-name"
                  value={signupForm.name}
                  onChange={(e) => handleSignupFieldChange('name', e.target.value)}
                  required
                  maxLength={200}
                  placeholder="Your full name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="signup-email">Email</label>
                <input
                  type="email"
                  id="signup-email"
                  value={signupForm.email}
                  onChange={(e) => handleSignupFieldChange('email', e.target.value)}
                  required
                  maxLength={320}
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="signup-company">Company / Organization</label>
                <input
                  type="text"
                  id="signup-company"
                  value={signupForm.company}
                  onChange={(e) => handleSignupFieldChange('company', e.target.value)}
                  required
                  maxLength={200}
                  placeholder="Company or organization name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="signup-role">Role</label>
                <SelectField
                  id="signup-role"
                  value={signupForm.role}
                  onChange={(v) => handleSignupFieldChange('role', v)}
                  options={ROLE_SELECT_OPTIONS}
                  placeholder="Select your role"
                  required
                  invalid={roleInvalid}
                />
              </div>
              <div className="form-group">
                <label htmlFor="signup-website">Website (optional)</label>
                <input
                  type="text"
                  id="signup-website"
                  value={signupForm.website}
                  onChange={(e) => handleSignupFieldChange('website', e.target.value)}
                  maxLength={500}
                  placeholder="example.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="signup-extra">Additional details (optional)</label>
                <textarea
                  id="signup-extra"
                  value={signupForm.extraDetails}
                  onChange={(e) => handleSignupFieldChange('extraDetails', e.target.value)}
                  maxLength={2000}
                  placeholder="Tell us about your use case or any other relevant information"
                  rows={4}
                />
              </div>
              <div className="signup-form-actions modal-actions">
                <button
                  type="button"
                  className="button-secondary"
                  onClick={closeSignupModal}
                  disabled={signupSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className="button-primary" disabled={signupSubmitting}>
                  {signupSubmitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </form>
          )}
        </Modal>
      )}
    </div>
  );
};

export default Login;
